import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, Trash2, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useCart } from '../../contexts/CartContext';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';

// Global cart icon + slide-over sheet for the client portal header.
export const CartSheet = () => {
  const navigate = useNavigate();
  const {
    cart,
    cartOpen,
    updateQuantity,
    setItemQuantity,
    removeFromCart,
    getCartTotal,
    getCartSavings,
    getCartItemCount,
    openCart,
    closeCart,
    formatAvailableDays,
  } = useCart();

  return (
    <Sheet open={cartOpen} onOpenChange={(open) => (open ? openCart() : closeCart())}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="relative" data-testid="global-cart-btn">
          <ShoppingCart className="h-5 w-5" />
          {getCartItemCount() > 0 && (
            <span className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center rounded-full text-xs text-white bg-amber-500">
              {getCartItemCount() > 99 ? '99+' : getCartItemCount()}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Your Cart
          </SheetTitle>
          <SheetDescription>
            {cart.length === 0 ? 'Your cart is empty' : `${getCartItemCount()} item(s) in your cart`}
          </SheetDescription>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-8">
            <ShoppingCart className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">Your cart is empty</p>
            <Button onClick={() => { closeCart(); navigate('/client/shop'); }} className="bg-amber-500 hover:bg-amber-600">
              Start Shopping
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-auto py-4">
              {cart.map((item) => (
                <div key={item.id} className="flex items-center gap-3 py-3 border-b">
                  {item.image_url && (
                    <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    {/* Show pricing with discount if applicable */}
                    {item.has_discount ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-xs line-through">${item.wholesale_price?.toFixed(2)}</span>
                          <Badge className="bg-green-100 text-green-800 text-xs px-1 py-0">{item.discount_percent}% off</Badge>
                        </div>
                        <p className="text-green-600 text-sm font-medium">${item.your_price?.toFixed(2)}</p>
                      </div>
                    ) : (
                      <p className="text-amber-600 text-sm">${item.wholesale_price?.toFixed(2)}</p>
                    )}

                    {/* Delivery Days Info */}
                    <div className="flex items-center gap-1 mt-1">
                      <Calendar className="h-3 w-3 text-gray-400" />
                      <span className={`text-xs ${
                        item.available_days && item.available_days.length > 0 ? 'text-amber-600' : 'text-gray-500'
                      }`}>
                        {formatAvailableDays(item.available_days)}
                      </span>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2 mt-2">
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.id, -1)}>
                        <Minus className="h-3 w-3" />
                      </Button>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => setItemQuantity(item.id, parseInt(e.target.value) || 1)}
                        className="w-14 h-7 text-center border rounded text-sm"
                      />
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.id, 1)}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-right">
                    {/* Line item total using discounted price if available */}
                    <p className="font-medium">
                      ${((item.your_price || item.wholesale_price) * item.quantity).toFixed(2)}
                    </p>
                    {item.has_discount && (
                      <p className="text-xs text-green-600">
                        Save ${(item.discount_amount * item.quantity).toFixed(2)}
                      </p>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-500 hover:text-red-600 mt-1"
                      onClick={() => removeFromCart(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Footer */}
            <div className="border-t pt-4 space-y-4">
              {/* Show savings if any discounts applied */}
              {getCartSavings() > 0 && (
                <div className="flex justify-between items-center text-sm text-green-600 bg-green-50 p-2 rounded">
                  <span>Your Savings:</span>
                  <span className="font-medium">-${getCartSavings().toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-lg font-semibold">
                <span>Total:</span>
                <span className="text-amber-600">${getCartTotal().toFixed(2)}</span>
              </div>
              <Button className="w-full bg-amber-500 hover:bg-amber-600" onClick={() => { closeCart(); navigate('/client/checkout'); }}>
                Proceed to Checkout
              </Button>
              <Button variant="outline" className="w-full" onClick={() => { closeCart(); navigate('/client/shop'); }}>
                Continue Shopping
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
